/* ==========================================================================
   ux-luggage-trip — the photo row's stepping and its story-style progress.

   Loaded only from sections/ux-luggage-trip.liquid, and only when the row has
   more than one card.

   The slide is the theme's own: assets/vendor.js bundles Flickity and exposes
   it as window.theme.Flickity — the engine every slideshow on the storefront
   already runs on — so the cards move the way the rest of the theme moves, a
   real drag with momentum and snapping, wrapping at both ends. assets/theme.css
   carries Flickity's base styles; the section's sheet only lets the viewport
   overflow to the right, where the row runs off the screen.

   The timer is the stylesheet's: the selected bar's fill is a CSS animation on
   `--ux-lt-duration`, and its `animationend` is what steps the row. Pausing is
   `animation-play-state`, so a paused bar holds its fill where it is and picks
   up from there. This element owns only which bar is selected
   (`aria-current`), the press on a bar, the reasons to pause — hover (unless the
   section's "Pause on hover" is off), keyboard focus,
   a drag, the row being off-screen or the tab hidden, a card selected in the
   theme editor — and the teardown.

   Autoplay is off when the section's setting is, and when the visitor asks for
   reduced motion: the selected bar is then drawn full and the row moves only
   when it is dragged or a bar is pressed.

   Without this file, or without Flickity, the row is the first cards at rest
   with the first bar full — the frames' own still.
   ========================================================================== */

if (!customElements.get('ux-luggage-trip')) {
  customElements.define(
    'ux-luggage-trip',
    class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;

        this.controller = new AbortController();
        const { signal } = this.controller;

        this.slidesEl = this.querySelector('.ux-luggage-trip__slides');
        this.slides = Array.from(this.querySelectorAll('[data-slide]'));
        this.bars = Array.from(this.querySelectorAll('[data-bar]'));
        this.pauses = new Set();

        const Flickity = window.theme?.Flickity;

        if (this.slides.length < 2 || !this.slidesEl || !Flickity) return;

        this.flkty = new Flickity(this.slidesEl, {
          cellSelector: '[data-slide]',
          cellAlign: 'left',
          wrapAround: true,
          adaptiveHeight: false,
          prevNextButtons: false,
          pageDots: false,
        });

        this.flkty.on('select', () => this.select());
        this.releasedAt = -Infinity;
        this.flkty.on('pointerDown', () => {
          this.pressing = true;
          this.pause('drag');
        });
        this.flkty.on('pointerUp', () => {
          this.pressing = false;
          this.releasedAt = performance.now();
          this.resume('drag');
        });

        this.bars.forEach((bar, index) => {
          bar.addEventListener('click', () => this.flkty.select(index), { signal });
        });

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.autoplay = this.dataset.autoplay === 'true' && !reducedMotion.matches;

        if (this.autoplay) {
          this.addEventListener(
            'animationend',
            (event) => {
              if (event.animationName === 'ux-luggage-trip-fill') this.flkty.next();
            },
            { signal }
          );

          if (this.dataset.pauseOnHover !== 'false') {
            this.addEventListener('mouseenter', () => this.pause('hover'), { signal });
            this.addEventListener('mouseleave', () => this.resume('hover'), { signal });
          }
          /* Keyboard focus only: Flickity focuses the row on every press and a
             pressed bar keeps focus, so a pointer focus would hold the row
             paused long after the drag or the press is over. Flickity's focus
             is a script call, which Chrome can match as :focus-visible, so the
             last input decides instead. */
          document.addEventListener('keydown', () => (this.keyboard = true), { signal, capture: true });
          document.addEventListener('pointerdown', () => (this.keyboard = false), { signal, capture: true });
          this.addEventListener(
            'focusin',
            () => {
              if (this.keyboard) this.pause('focus');
            },
            { signal }
          );
          this.addEventListener(
            'focusout',
            (event) => {
              if (!this.contains(event.relatedTarget)) this.resume('focus');
            },
            { signal }
          );

          document.addEventListener(
            'visibilitychange',
            () => (document.hidden ? this.pause('hidden') : this.resume('hidden')),
            { signal }
          );

          if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(([entry]) => {
              if (entry.isIntersecting) this.resume('offscreen');
              else this.pause('offscreen');
            });
            this.observer.observe(this);
          }

          this.setAttribute('data-playing', '');
        }

        /* The theme editor: a card selected in the sidebar is brought to the
           front and held there until it is deselected. */
        this.addEventListener(
          'shopify:block:select',
          (event) => {
            const index = this.slides.indexOf(event.target.closest('[data-slide]'));

            /* A mouse drag in the preview ends in a click on the card it
               started from, and the editor selects that card's block: left
               alone, the row would jump straight back to it. A touch drag
               fires no click, which is why only the mouse was affected. */
            if (index < 0 || this.pressing || performance.now() - this.releasedAt < 1000) return;

            this.flkty.select(index, false, true);
            this.pause('editor');
          },
          { signal }
        );
        this.addEventListener('shopify:block:deselect', () => this.resume('editor'), { signal });

        this.select();
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.controller = null;
        this.observer?.disconnect();
        this.observer = null;
        this.flkty?.destroy();
        this.flkty = null;
      }

      select() {
        const index = this.flkty ? this.flkty.selectedIndex : 0;

        this.bars.forEach((bar, barIndex) => {
          if (barIndex === index) bar.setAttribute('aria-current', 'true');
          else bar.removeAttribute('aria-current');
        });

        this.restart();
      }

      /* A fresh fill for the newly selected bar, from empty, even when the
         same bar is selected again. */
      restart() {
        if (!this.autoplay) return;

        this.removeAttribute('data-playing');
        void this.offsetWidth;
        this.setAttribute('data-playing', '');
      }

      pause(reason) {
        this.pauses.add(reason);
        this.toggleAttribute('data-paused', true);
      }

      resume(reason) {
        this.pauses.delete(reason);
        this.toggleAttribute('data-paused', this.pauses.size > 0);
      }
    }
  );
}
